import type { TableAction, TableActionGroup } from '@shared/types/data-table'
import type { PaymentLink } from '@shared/types/payment-links'
import { createBaseActions, mergeActions, type BaseActionHandlers } from './base-actions'
import { CheckCircle, XCircle, Archive } from 'lucide-react'

interface PaymentLinkSpecificHandlers {
  onViewDetails?: (link: PaymentLink) => void
  onChangeName?: (link: PaymentLink) => void
  onCopyLinkUrl?: (link: PaymentLink) => void
  onPreviewLink?: (link: PaymentLink) => void
  onUpdate?: (link: PaymentLink) => void
  onDeactivate?: (link: PaymentLink) => void
  onAddQrCode?: (link: PaymentLink) => void
  onAddNfcTag?: (link: PaymentLink) => void
  onBulkActivate?: (linkIds: string[]) => void
  onBulkDeactivate?: (linkIds: string[]) => void
  onBulkArchive?: (linkIds: string[]) => void
}

export type PaymentLinkActionHandlers = BaseActionHandlers<PaymentLink> & PaymentLinkSpecificHandlers

const createPaymentLinkBulkActions = (handlers: PaymentLinkActionHandlers) => [
  {
    key: 'activate',
    label: 'Activate Links',
    icon: <CheckCircle className="h-4 w-4" />,
    onClick: (ids: string[]) => handlers.onBulkActivate?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
  {
    key: 'deactivate',
    label: 'Deactivate Links',
    icon: <XCircle className="h-4 w-4" />,
    onClick: (ids: string[]) => handlers.onBulkDeactivate?.(ids),
    variant: 'destructive' as const,
    disabled: (ids: string[]) => ids.length === 0,
  },
  {
    key: 'archive',
    label: 'Archive Links',
    icon: <Archive className="h-4 w-4" />,
    onClick: (ids: string[]) => handlers.onBulkArchive?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
]

// Row actions for tables - quick access operations
const createPaymentLinkRowActions = (handlers: PaymentLinkActionHandlers): TableAction<PaymentLink>[] => [
  {
    key: 'view-details',
    label: 'View Details',
    onClick: (link: PaymentLink) => handlers.onViewDetails?.(link),
  },
  {
    key: 'copy-link-url',
    label: 'Copy Link URL',
    onClick: (link: PaymentLink) => handlers.onCopyLinkUrl?.(link),
  },
  {
    key: 'preview-link',
    label: 'Preview Link',
    onClick: (link: PaymentLink) => handlers.onPreviewLink?.(link),
  },
]

// Panel actions for detail pages - grouped to match design
const createPaymentLinkPanelActionGroups = (handlers: PaymentLinkActionHandlers): TableActionGroup<PaymentLink>[] => [
  {
    label: 'Actions',
    actions: [
      {
        key: 'update-link',
        label: 'Update Link',
        onClick: (link: PaymentLink) => handlers.onUpdate?.(link),
      },
      {
        key: 'change-name',
        label: 'Change name',
        onClick: (link: PaymentLink) => handlers.onChangeName?.(link),
      },
      {
        key: 'add-qr-code',
        label: 'Add QR Code',
        onClick: (link: PaymentLink) => handlers.onAddQrCode?.(link),
      },
      {
        key: 'add-nfc-tag',
        label: 'Add NFC Tag',
        onClick: (link: PaymentLink) => handlers.onAddNfcTag?.(link),
      },
      {
        key: 'deactivate-link',
        label: 'Deactivate Link',
        onClick: (link: PaymentLink) => handlers.onDeactivate?.(link),
        variant: 'destructive' as const,
        condition: (link: PaymentLink) => link.status === 'active',
      },
    ],
  },
  {
    label: 'Connections',
    actions: [
      {
        key: 'view-link-preview',
        label: 'View link preview',
        onClick: (link: PaymentLink) => handlers.onPreviewLink?.(link),
      },
    ],
  },
]

export function getPaymentLinkActions(handlers: PaymentLinkActionHandlers) {
  const baseActions = createBaseActions('payment-link', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: false,
    showAddNote: false,
    showCopyId: false,
    showExport: true,
  })

  return mergeActions(
    baseActions,
    {
      bulkActions: createPaymentLinkBulkActions(handlers),
      rowActions: createPaymentLinkRowActions(handlers),
    }
  )
}

export function getPaymentLinkPanelActions(handlers: PaymentLinkActionHandlers): TableActionGroup<PaymentLink>[] {
  return createPaymentLinkPanelActionGroups(handlers)
}
